import { Form, Select, Modal, Button, message, Divider, Space } from "antd";
import { CloseOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import AnimateItems from "../../../utils/AnimateItems";
import axios from "axios";
import PersonaForm from "./persona/PersonaForm";
import { useAuth } from "../../../layout/auth";

// Which verificación... er, visita fields belong to "whichever testigo
// this modal instance is currently editing." Testigo 1 and Testigo 2 are
// fully independent, so each gets its own field set.
const fieldsFor = (title) => {
  if (title === "Testigo 1") return { P: "PT1", Dato: "DatoPT1", Domicilio: "DomicilioPT1" };
  if (title === "Testigo 2") return { P: "PT2", Dato: "DatoPT2", Domicilio: "DomicilioPT2" };
  return { P: null, Dato: null, Domicilio: null };
};

const pickFields = (source, fields) => ({
  [fields.P]: source?.[fields.P] ?? null,
  [fields.Dato]: source?.[fields.Dato] ?? null,
  [fields.Domicilio]: source?.[fields.Domicilio] ?? null,
});

export default function TestigoForm(props) {
  const { title, visible, mainData: visita, setMainData: setVisita, setVisible } = props;
  const [personaFormVisible, setPersonaFormVisible] = useState(false);
  const [foliosPersona, setFoliosPersona] = useState();
  const [domiciliosPersona, setDomiciliosPersona] = useState();
  const [personasList, setPersonasList] = useState();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [personaEditing, setPersonaEditing] = useState();
  const auth = useAuth();

  const fields = fieldsFor(title);

  // Snapshot of this testigo's fields exactly as they were when the modal
  // opened, so "Cancelar" can genuinely revert to this — including undoing
  // an already-saved new persona created during this session.
  const originalFieldsRef = useRef();

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      originalFieldsRef.current = pickFields(visita, fields);
    }
    // Deliberately only depends on `visible`/`title`: captured once when
    // the modal opens, not every time `visita` changes afterward (which
    // happens as soon as a persona auto-saves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, title]);

  // Extracted so it can be reused both on initial load and right after a
  // new persona is created (so the dropdown includes them immediately).
  const fetchPersonasList = () => {
    const personasPromise =
      auth.Rol === "director"
        ? axios.get(`${process.env.REACT_APP_API_URL}/personas`)
        : axios.get(`${process.env.REACT_APP_API_URL}/personas/usuario/${auth.UID}`);

    return personasPromise
      .then((p) => {
        setPersonasList(p.data.filter((persona) => persona.TipoPersonaID === 1));
      })
      .catch((error) => {
        console.log(error);
        message.error(error.message);
      });
  };

  // The single source of truth for persisting this visita. Used by the
  // main "Guardar" button, immediately after a new persona is created,
  // and by "Cancelar" (with an override snapshot) to genuinely revert.
  const saveVisita = (overrides) => {
    const visitData = { ...visita, ...form.getFieldsValue(), ...overrides };
    return axios
      .put(`${process.env.REACT_APP_API_URL}/visitas/${visita.VisitaID}`, visitData)
      .then(() =>
        axios.get(`${process.env.REACT_APP_API_URL}/visitas/${visita.VerificacionID}`).then((res) => {
          setVisita(res.data);
          return true;
        })
      )
      .catch((err) => {
        console.log(err);
        const errorMsg = err.response?.data?.message || "Error al actualizar la visita";
        message.error(errorMsg);
        return false;
      });
  };

  // Extracted so it can be reused by the Select's onChange and right after
  // a new persona is created. Returns a promise so callers can chain a
  // save immediately after the related data has finished loading.
  const handlePersonaSelected = (value) => {
    if (value) {
      return Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${value}`),
      ])
        .then(([f, dm]) => {
          setFoliosPersona(f.data);
          setDomiciliosPersona(dm.data);
          form.setFieldsValue({
            [fields.Dato]: f.data[0]?.DatoID,
            [fields.Domicilio]: dm.data[0]?.DatoID,
          });
        })
        .catch((error) => {
          console.log(error);
          message.error("Error al cargar los datos");
        });
    } else {
      setFoliosPersona(null);
      setDomiciliosPersona(null);
      form.setFieldsValue({
        [fields.Dato]: null,
        [fields.Domicilio]: null,
      });
      return Promise.resolve();
    }
  };

  // Called by PersonaForm right after a brand-new persona is saved:
  // select them, load their (empty) data, and then IMMEDIATELY persist the
  // link to this visita (the same save the "Guardar" button does) — so the
  // persona is genuinely linked in the database right away. If the user
  // later clicks "Cancelar", this save gets reverted via originalFieldsRef.
  const handlePersonaCreated = (newPersonaId) => {
    form.setFieldsValue({ [fields.P]: newPersonaId });
    handlePersonaSelected(newPersonaId).then(() => {
      saveVisita().then((ok) => {
        if (ok) {
          fetchPersonasList();
          message.success("La nueva persona quedó guardada y vinculada a esta visita.");
        }
      });
    });
  };

  // Called by PersonaForm right after a dato/identificación is added or
  // edited for the persona currently being edited. Here there's no
  // generic "dato" field, only Folio and Domicilio (both backed by the
  // same `datos` table, filtered differently by the backend) — so we
  // re-run both of this screen's own queries and check which one actually
  // contains the new/edited DatoID, then select that field. If it belongs
  // to neither (e.g. a TipoDatoID excluded by both endpoints — see
  // datos.js), warn instead of going silent.
  const handleDatoAdded = (newDatoId) => {
    if (!personaEditing) return;
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${personaEditing}`),
      axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${personaEditing}`),
    ])
      .then(([f, dm]) => {
        setFoliosPersona(f.data);
        setDomiciliosPersona(dm.data);

        if (f.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ [fields.Dato]: newDatoId });
          message.success("El nuevo folio ya quedó seleccionado en el formulario.");
        } else if (dm.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ [fields.Domicilio]: newDatoId });
          message.success("El nuevo domicilio ya quedó seleccionado en el formulario.");
        } else {
          message.warning("El tipo de identificación que agregaste no aplica como Folio ni Domicilio para este testigo.");
        }
      })
      .catch((error) => {
        console.log(error);
        message.error("Error al actualizar folios/domicilios");
      });
  };

  // "Cancelar" reverts this testigo's fields back to exactly what they
  // were when the modal opened — genuinely writing that back to the
  // database, since a persona created during this session may have
  // already been auto-saved and linked.
  const handleCancel = () => {
    saveVisita(originalFieldsRef.current).then((ok) => {
      if (ok) setVisible(false);
    });
  };

  useEffect(() => {
    visible &&
      Promise.all([
        fetchPersonasList(),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/folios/${visita?.[fields.P]}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/domicilios/${visita?.[fields.P]}`),
      ])
        .then(([, f, d]) => {
          setFoliosPersona(f.data);
          setDomiciliosPersona(d.data);
          form.setFieldsValue(visita);
        })
        .catch((error) => {
          console.log(error);
          message.error(error.message);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visita, form, visible, title]);

  const clearSelectProps = {
    style: { width: "100%" },
    allowClear: true,
    clearIcon: <CloseOutlined style={{ color: "red" }} />,
    showSearch: true,
    filterOption: (input, option) => option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0,
  };

  return (
    <Modal title={title} visible={visible} onCancel={() => setVisible(false)} footer={null} width={800} centered>
      <Form form={form} layout="vertical">
        <AnimateItems delay={0.15}>
          <Form.Item name={fields.P} label="Testigo">
            <Select
              onChange={handlePersonaSelected}
              {...clearSelectProps}
              open={openDropdown}
              onDropdownVisibleChange={(visibility) => setOpenDropdown(visibility)}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ float: "right" }}>
                    <Divider style={{ margin: "4px 0" }} />
                    <Space direction="horizontal">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                          setPersonaEditing(form.getFieldValue(fields.P));
                          setOpenDropdown(false);
                          setTimeout(() => {
                            setPersonaFormVisible(true);
                          }, 250);
                        }}
                      >
                        Editar persona
                      </Button>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setPersonaEditing(null);
                          setOpenDropdown(false);
                          setTimeout(() => {
                            setPersonaFormVisible(true);
                          }, 250);
                        }}
                      >
                        Agregar persona
                      </Button>
                    </Space>
                  </div>
                </>
              )}
            >
              {personasList?.map((persona) => (
                <Select.Option key={persona.PersonaID} value={persona.PersonaID}>
                  {persona.NombreCompleto}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={fields.Dato} label="Folio de identificación del testigo">
            <Select {...clearSelectProps}>
              {foliosPersona?.map((folio) => (
                <Select.Option key={folio.DatoID} value={folio.DatoID}>
                  {folio.TipoDato_Corto + ": " + folio.Dato}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={fields.Domicilio} label="Domicilio del testigo">
            <Select {...clearSelectProps}>
              {domiciliosPersona?.map((domicilio) => (
                <Select.Option key={domicilio.DatoID} value={domicilio.DatoID}>
                  {domicilio.TipoDato_Corto + ": " + domicilio.Dato}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </AnimateItems>
      </Form>
      <PersonaForm
        setPersonasList={setPersonasList}
        setDatosPersona={() => {}}
        setTelefonosFijos={() => {}}
        setTelefonosMoviles={() => {}}
        setCorreosPersona={() => {}}
        setFoliosPersona={setFoliosPersona}
        setDomiciliosPersona={setDomiciliosPersona}
        personaEditing={personaEditing}
        setPersonaEditing={setPersonaEditing}
        personaFormVisible={personaFormVisible}
        setPersonaFormVisible={setPersonaFormVisible}
        origin="visita"
        refreshPersonasList={fetchPersonasList}
        onPersonaCreated={handlePersonaCreated}
        onDatoChanged={handleDatoAdded}
        onCancel={() => setPersonaFormVisible(false)}
      />
      <Button
        type="primary"
        onClick={() => {
          saveVisita().then((ok) => {
            if (ok) setVisible(false);
          });
        }}
      >
        Guardar
      </Button>
      <Button onClick={handleCancel}>Cancelar</Button>
    </Modal>
  );
}